import { useEffect, useRef, useState } from 'react';
import { useAudioDataContext } from '@context/AudioDataContext';
import { Cancel as CancelIcon, ErrorOutlined as ErrorOutlineIcon } from '@mui/icons-material';
import { Box, CircularProgress, Fade, Tooltip } from '@mui/material';
import type { HealthState } from '@services/audio';

/** Total number of bars in the meter */
const BAR_COUNT = 5;
/** Width of each bar (32px total width) */
const BAR_WIDTH = 24;
/** Vertical gap between bars */
const BAR_GAP = 2;
/** Height of each bar */
const BAR_HEIGHT = 4;
/** How often we sample the level */
const UPDATE_INTERVAL = 80; // ms

/** Tooltip messages per health state */
const HEALTH_TOOLTIPS: Record<HealthState, string> = {
  idle: '',
  initializing: 'Checking microphone...',
  healthy: 'We can hear you! Microphone is working properly',
  degraded: 'Low audio detected. Check that your microphone isn\u2019t muted',
  failed:
    'No audio detected. Check your microphone connection or try switching to a different microphone',
};

/** Visual config per health state */
const HEALTH_CONFIG: Record<
  HealthState,
  { barOpacity: number; overlay: 'spinner' | 'warning' | 'error' | null }
> = {
  idle: { barOpacity: 1, overlay: null },
  initializing: { barOpacity: 0.3, overlay: 'spinner' },
  healthy: { barOpacity: 1, overlay: null },
  degraded: { barOpacity: 0.3, overlay: 'warning' },
  failed: { barOpacity: 0, overlay: 'error' },
};

interface AudioLevelMeterProps {
  /** Current health state — drives bar opacity and overlay icons */
  healthState: HealthState;
}

/**
 * Compact vertical level meter with integrated health status overlay.
 * Bars bounce green when healthy (no extra icon needed). Non-healthy
 * states dim the bars and overlay a status icon (spinner / warning / error).
 *
 * Reads from AudioDataContext (fed by the device service's MicLevelMeter
 * during pre-recording, and by RecordingService during recording).
 */
export const AudioLevelMeter: React.FC<AudioLevelMeterProps> = ({ healthState }) => {
  const { audioData } = useAudioDataContext();
  const audioDataRef = useRef(audioData);
  const [activeBars, setActiveBars] = useState(0);

  const isActive = healthState !== 'idle';
  const config = HEALTH_CONFIG[healthState];
  const tooltipTitle = HEALTH_TOOLTIPS[healthState];

  useEffect(() => {
    audioDataRef.current = audioData;
  }, [audioData]);

  // Reset when deactivated
  useEffect(() => {
    if (!isActive) {
      setActiveBars(0);
    }
  }, [isActive]);

  // Sample the level at a regular interval
  useEffect(() => {
    if (!isActive) return;

    const interval = setInterval(() => {
      const level = audioDataRef.current; // 0-100
      const filled = Math.round((level / 100) * BAR_COUNT);
      setActiveBars(filled);
    }, UPDATE_INTERVAL);

    return () => clearInterval(interval);
  }, [isActive]);

  const renderOverlayIcon = () => {
    switch (config.overlay) {
      case 'spinner':
        return <CircularProgress size={16} thickness={5} sx={{ color: 'text.disabled' }} />;
      case 'warning':
        return <ErrorOutlineIcon sx={{ fontSize: 18, color: 'warning.main' }} />;
      case 'error':
        return <CancelIcon sx={{ fontSize: 20, color: 'error.main' }} />;
      default:
        return null;
    }
  };

  const meter = (
    <Box
      sx={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {/* Bars layer */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column-reverse', // bottom bar = index 0
          gap: `${BAR_GAP}px`,
          px: 0.5,
          py: 0.25,
          opacity: config.barOpacity,
          transition: 'opacity 300ms ease',
        }}
      >
        {Array.from({ length: BAR_COUNT }, (_, index) => {
          const isLit = isActive && index < activeBars;
          return (
            <Box
              key={index}
              sx={{
                width: `${BAR_WIDTH}px`,
                height: `${BAR_HEIGHT}px`,
                borderRadius: 0.5,
                border: '1px solid',
                borderColor: isLit ? 'success.main' : 'grey.300',
                backgroundColor: isLit ? 'success.main' : 'grey.200',
                transition: 'background-color 80ms linear',
              }}
            />
          );
        })}
      </Box>

      {/* Status overlay */}
      <Fade in={config.overlay !== null} timeout={300}>
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {renderOverlayIcon()}
        </Box>
      </Fade>
    </Box>
  );

  if (tooltipTitle) {
    return <Tooltip title={tooltipTitle}>{meter}</Tooltip>;
  }

  return meter;
};
